import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { SheetPortal } from '../../shared/ui/SheetPortal';
import { useI18nStore } from '../../shared/i18n/i18nStore';

/**
 * "Where do I find the code?" — one real receipt per Argentine bank/wallet with
 * the 22-character code boxed in red, plus the field name each app uses. The
 * pictures live in public/receipts/ar and load only when this sheet opens.
 */
type Bank = { id: string; name: string; img: string; warn?: boolean };
const BANKS: Bank[] = [
  { id: 'mercado-pago', name: 'Mercado Pago', img: '/receipts/ar/1.png', warn: true },
  { id: 'naranja-x', name: 'Naranja X', img: '/receipts/ar/2.png' },
  { id: 'galicia', name: 'Galicia', img: '/receipts/ar/3.png' },
  { id: 'banco-macro', name: 'Banco Macro', img: '/receipts/ar/4.png' },
  { id: 'uala', name: 'Ualá', img: '/receipts/ar/5.png' },
  { id: 'astropay', name: 'AstroPay', img: '/receipts/ar/6.png' },
  { id: 'banco-del-sol', name: 'Banco del sol', img: '/receipts/ar/7.png' },
];
const OTHER_FIELD_NAMES = ['Código de identificación', 'Número de transacción', 'ID Operación', 'Coelsa ID', 'Identificador de Transferencia'];

/** Renders `"Field name" on Bank · ...` with the quoted field names in bold. */
function CalledList({ text }: { text: string }) {
  const parts = text.split(/("[^"]+")/g);
  return <p className="receipt-ex-called-list">{parts.map((part, i) => (part.startsWith('"') ? <b key={i}>{part}</b> : <span key={i}>{part}</span>))}</p>;
}

export function ReceiptExamplesSheet({ onClose, onSupport }: { onClose: () => void; onSupport: () => void }) {
  const t = useI18nStore((s) => s.t);
  const [tab, setTab] = useState<string>(BANKS[0].id);
  const bank = BANKS.find((b) => b.id === tab);
  return (
    <SheetPortal>
      <div className="sheet-backdrop receipt-ex-backdrop" role="presentation" onMouseDown={onClose}>
        <section className="receipt-ex" role="dialog" aria-modal="true" aria-label={t('receiptExTitle')} data-receipt-examples-react="true" onMouseDown={(e) => e.stopPropagation()}>
          <header className="receipt-ex-head">
            <h2>{t('receiptExTitle')}</h2>
            <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
          </header>
          <p className="receipt-ex-sub">{t('receiptExSub')}</p>
          <div className="receipt-ex-tabs" role="tablist">
            {[...BANKS.map((b) => ({ id: b.id, name: b.name })), { id: 'others', name: t('receiptExOthers') }].map((item) => (
              <button key={item.id} type="button" role="tab" className={`receipt-ex-tab${tab === item.id ? ' is-active' : ''}`} aria-selected={tab === item.id} data-receipt-tab={item.id} onClick={() => setTab(item.id)}>{item.name}</button>
            ))}
          </div>
          <div className="receipt-ex-body">
            {bank ? (
              <>
                {bank.warn ? (
                  <p className="receipt-ex-warning" role="note"><AlertTriangle size={16} aria-hidden="true" /><span>{t('receiptExMpWarning')}</span></p>
                ) : null}
                <img className="receipt-ex-img" src={bank.img} alt={t('receiptExImgAlt').replace('%bank%', bank.name)} loading="lazy" decoding="async" />
              </>
            ) : (
              <div className="receipt-ex-others">
                <p>{t('receiptExOthersIntro')}</p>
                <p>{t('receiptExOthersNames')}</p>
                <ul>{OTHER_FIELD_NAMES.map((name) => <li key={name}>{name}</li>)}</ul>
                <p>{t('receiptExOthersOutro')}</p>
              </div>
            )}
            <section className="receipt-ex-called">
              <h3>{t('receiptExCalled')}</h3>
              <CalledList text={t('receiptExCalledList')} />
            </section>
            <button type="button" className="unpaid-link receipt-ex-support" onClick={onSupport}>{t('receiptExNoBank')}</button>
          </div>
          <button type="button" className="primary-button receipt-ex-done" onClick={onClose}>{t('receiptExGotIt')}</button>
        </section>
      </div>
    </SheetPortal>
  );
}
